import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivityItem, ActivityFilter, matchesActivityFilter } from './cloud-activity.model';
import { EnrichmentService, EnrichmentView, EvidenceSummary } from '../../core/services/enrichment.service';

/**
 * Shared Activity tab UI — a reverse-chronological operational timeline for one
 * service type. Renders filter chips (only those with data), a loading
 * skeleton, the timeline, and a service-aware empty state. Items can be
 * clicked to drill into the related resource (parent wires `itemSelect`).
 */
@Component({
  selector: 'app-cloud-activity-feed',
  template: `
    <div class="p-6">

      <!-- Filter chips (only categories with data) -->
      <div *ngIf="!loading && items.length > 0" class="mb-4 flex flex-wrap items-center gap-1.5">
        <button
          *ngFor="let f of availableFilters"
          type="button"
          (click)="filter = f.key"
          class="rounded-full border px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
          [class.border-violet-300]="filter === f.key"
          [class.bg-violet-50]="filter === f.key"
          [class.text-violet-700]="filter === f.key"
          [class.border-slate-200]="filter !== f.key"
          [class.text-slate-500]="filter !== f.key"
          [class.hover:bg-slate-50]="filter !== f.key"
        >
          {{ f.label }} <span class="text-slate-400">{{ f.count }}</span>
        </button>
      </div>

      <!-- Loading skeleton -->
      <div *ngIf="loading" class="space-y-2" aria-hidden="true">
        <div *ngFor="let _ of [1,2,3,4]" class="h-12 animate-pulse rounded-lg bg-slate-100"></div>
        <span class="sr-only" role="status">Loading activity…</span>
      </div>

      <!-- Timeline -->
      <ul *ngIf="!loading && filtered.length > 0" class="space-y-1">
        <li *ngFor="let item of filtered">
          <div
            class="flex items-start gap-3 rounded-lg px-3 py-2.5"
            [class.cursor-pointer]="item.clickable"
            [class.hover:bg-slate-50]="item.clickable"
            [attr.role]="item.clickable ? 'button' : null"
            [attr.tabindex]="item.clickable ? 0 : null"
            (click)="item.clickable && itemSelect.emit(item)"
            (keydown.enter)="item.clickable && itemSelect.emit(item)"
            [attr.aria-label]="item.clickable ? ('View ' + item.resourceId) : null"
          >
            <span class="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full" [class]="dotClass(item.severity)"></span>
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span class="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide" [class]="badgeClass(item.severity)">{{ item.eventType }}</span>
                <span class="truncate font-mono text-[11px] text-slate-500">{{ item.resourceId }}</span>
                <span *ngIf="item.region" class="flex-shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500" [title]="'Region: ' + item.region">{{ item.region }}</span>
              </div>
              <p class="mt-0.5 text-xs text-slate-700">{{ item.summary }}</p>
            </div>
            <time class="mt-0.5 flex-shrink-0 whitespace-nowrap text-[11px] text-slate-400">{{ item.timestamp | cloudDate }}</time>
          </div>

          <!-- Inline read-only diagnosis for finding-derived items -->
          <div *ngIf="item.findingId" class="pl-8 pr-3">
            <button
              type="button"
              (click)="toggleDiagnosis(item.findingId)"
              class="inline-flex items-center gap-1 py-1 text-[11px] font-medium text-violet-600 hover:text-violet-800"
              [attr.aria-expanded]="expandedFindingId === item.findingId"
            >
              <span class="inline-block w-2 text-slate-400">{{ expandedFindingId === item.findingId ? '▾' : '▸' }}</span>
              {{ expandedFindingId === item.findingId ? 'Hide diagnosis' : 'View diagnosis' }}
            </button>

            <div *ngIf="expandedFindingId === item.findingId" class="mt-1">
              <app-diagnosis-section [view]="enrichment" [loading]="enrichmentLoading"></app-diagnosis-section>

              <!-- Compact evidence provenance (from the persisted bundle) -->
              <div *ngIf="evidence" class="mb-2 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
                <span class="font-medium text-slate-600">Evidence</span>
                · {{ evidence.logSource || 'metrics only' }}
                · {{ evidence.totalErrorLines }} error line{{ evidence.totalErrorLines === 1 ? '' : 's' }}
                <span *ngIf="evidence.queries.length"> · {{ evidence.queries.length }} quer{{ evidence.queries.length === 1 ? 'y' : 'ies' }}</span>
                <span *ngIf="evidence.redactionApplied"> · redacted</span>
              </div>

              <!-- Deep link to the first-class finding detail surface -->
              <a [routerLink]="['/findings', item.findingId]" class="mb-6 inline-block text-[11px] font-medium text-violet-600 hover:text-violet-800">Open finding detail →</a>
            </div>
          </div>
        </li>
      </ul>

      <!-- Empty (filtered out) -->
      <div *ngIf="!loading && items.length > 0 && filtered.length === 0" class="py-10 text-center">
        <p class="text-sm font-medium text-slate-500">No {{ filter }} activity</p>
        <button type="button" (click)="filter = 'all'" class="mt-1 text-xs font-medium text-violet-600 hover:text-violet-700">Show all activity</button>
      </div>

      <!-- Empty (no data) — service-aware -->
      <div *ngIf="!loading && items.length === 0" class="py-12 text-center">
        <div class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
          <lucide-icon name="activity" [size]="22" class="text-slate-400" [strokeWidth]="1.5"></lucide-icon>
        </div>
        <p class="text-sm font-semibold text-slate-700">No recent {{ serviceLabel }} activity</p>
        <p class="mx-auto mt-1 max-w-sm text-xs text-slate-400">{{ emptyHint }}</p>
        <p *ngIf="note" class="mx-auto mt-2 max-w-sm text-xs text-amber-700">{{ note }}</p>
      </div>

    </div>
  `,
})
export class CloudActivityFeedComponent {
  @Input() items: ActivityItem[] = [];
  @Input() loading = false;
  /** Lower-case service noun used in the empty state, e.g. "DynamoDB". */
  @Input() serviceLabel = 'service';
  /** What feeds this service's activity — shown in the empty state. */
  @Input() emptyHint = 'Failures, throttles, and monitoring findings appear here as they happen.';
  /** Optional note when signal quality is intentionally limited for this service. */
  @Input() note: string | null = null;

  @Output() itemSelect = new EventEmitter<ActivityItem>();

  filter: ActivityFilter = 'all';

  // Inline diagnosis expansion (read-only; never blocks the feed)
  expandedFindingId: string | null = null;
  enrichment:        EnrichmentView | null = null;
  evidence:          EvidenceSummary | null = null;
  enrichmentLoading = false;
  private enrichmentSub?: Subscription;

  constructor(private readonly enrichment$: EnrichmentService) {}

  /** Toggles the inline diagnosis for a finding; loads it non-blockingly. */
  toggleDiagnosis(findingId: string): void {
    if (this.expandedFindingId === findingId) {
      this.expandedFindingId = null;
      this.enrichmentSub?.unsubscribe();
      return;
    }
    this.expandedFindingId = findingId;
    this.enrichment        = null;
    this.evidence          = null;
    this.enrichmentLoading = true;
    this.enrichmentSub?.unsubscribe();
    this.enrichmentSub = this.enrichment$.getEnrichment(findingId, { includeEvidence: true }).subscribe({
      next: ({ enrichment, evidence }) => {
        // Guard against a stale response after the user expanded another item.
        if (this.expandedFindingId !== findingId) return;
        this.enrichment        = enrichment;
        this.evidence          = evidence ?? null;
        this.enrichmentLoading = false;
      },
      error: () => {
        if (this.expandedFindingId !== findingId) return;
        this.enrichment        = null;
        this.evidence          = null;
        this.enrichmentLoading = false;
      },
    });
  }

  private readonly filterDefs: { key: ActivityFilter; label: string }[] = [
    { key: 'all',      label: 'All'           },
    { key: 'failures', label: 'Failures'      },
    { key: 'findings', label: 'Findings'      },
    { key: 'runs',     label: 'Runs'          },
    { key: 'config',   label: 'Config changes' },
  ];

  /** Only show a chip when it would match ≥1 item (always show "All"). */
  get availableFilters(): { key: ActivityFilter; label: string; count: number }[] {
    return this.filterDefs
      .map(d => ({ ...d, count: this.items.filter(i => matchesActivityFilter(i, d.key)).length }))
      .filter(d => d.key === 'all' || d.count > 0);
  }

  get filtered(): ActivityItem[] {
    return this.items.filter(i => matchesActivityFilter(i, this.filter));
  }

  dotClass(sev: string): string {
    if (sev === 'CRITICAL') return 'bg-red-500';
    if (sev === 'HIGH')     return 'bg-orange-400';
    if (sev === 'MEDIUM')   return 'bg-amber-400';
    if (sev === 'LOW')      return 'bg-blue-400';
    return 'bg-emerald-400'; // INFO (e.g. resolved / successful run)
  }

  badgeClass(sev: string): string {
    if (sev === 'CRITICAL') return 'bg-red-100 text-red-700';
    if (sev === 'HIGH')     return 'bg-orange-100 text-orange-700';
    if (sev === 'MEDIUM')   return 'bg-amber-100 text-amber-700';
    if (sev === 'LOW')      return 'bg-blue-100 text-blue-700';
    return 'bg-emerald-100 text-emerald-700';
  }
}
